import { EpocusRole } from "elephant-web/gql";

/**
 * Canonical ePOCUS UI capability table — the single source of truth for which
 * roles may perform which gated operation across the `/epocus` surface.
 *
 * Each capability is symbolic (one per gated operation) and maps to the ePOCUS
 * roles that possess it. UI code must gate through `useCan(capability)` /
 * `<RoleGate>` rather than checking a role inline.
 *
 * This table mirrors the backend grants 1:1. It derives from:
 *  - PNE-172 — RBAC Foundation (role enum + the base role table)
 *  - PNE-505 — ePOCUS resolver role guards + performer-lock 403s (the
 *              `@authUserRole(roles: [...])` decorators this table must match)
 *  - PNE-507 — PROGRAM_ADMIN org-scoped user management (api-admin Policy)
 *
 * Roles form a broad seniority chain (Performer → Reviewer → QA Admin →
 * Program Admin), but that chain is not a per-capability invariant: authoring
 * is clinician-only and user management is Program-Admin-only. The two
 * deliberate breaks are called out on their capabilities below and locked in by
 * `capabilities.test.ts` so they are not "fixed" by a well-meaning future edit.
 */
export const CAPABILITIES = {
  /**
   * Create a new encounter. Clinician action — performers only (PNE-505).
   * BREAK IN CHAIN: QA Admin does NOT get this — they are program oversight,
   * not clinicians (Open decision §1, resolved read-only). Do not add QaAdmin.
   */
  createEncounter: [EpocusRole.Performer, EpocusRole.PerformerReviewer],
  /** Edit one's own in-progress exam/worksheet. Performer-locked (PNE-505). */
  editOwnExam: [EpocusRole.Performer, EpocusRole.PerformerReviewer],
  /**
   * Claim an unassigned imported scan off the "To Document" queue. Clinician
   * action, as `createEncounter`. Enforced on push by the IMPORTED → DRAFT
   * transition rather than an `@authUserRole` decorator (PNE-561).
   */
  claimTriageEncounter: [EpocusRole.Performer, EpocusRole.PerformerReviewer],
  /** Raise a scan request. Asking for a scan is not performing one, so every
   * program member has it — narrowing this to Performer hides the request sheet
   * from the reviewers and admins the PRD puts it in front of. */
  requestScan: [
    EpocusRole.Performer,
    EpocusRole.Reviewer,
    EpocusRole.PerformerReviewer,
    EpocusRole.QaAdmin,
    EpocusRole.ProgramAdmin,
  ],
  startOrderedScan: [EpocusRole.Performer, EpocusRole.PerformerReviewer],
  /** Seed and clear the program's demo worklist. `seedEpocusWorklist` requires
   * PROGRAM_ADMIN or EPOCUS_ADMIN, so adding QaAdmin here shows a button whose
   * mutation then 403s. */
  seedWorklist: [EpocusRole.ProgramAdmin],
  /** Clear the program's seeded demo studies. `clearEpocusSeededDemoStudies`
   * requires PROGRAM_ADMIN or EPOCUS_ADMIN, as `seedWorklist`. */
  clearSeededDemoStudies: [EpocusRole.ProgramAdmin],
  /** Refer a patient onward. Performing and reviewing clinicians (PNE-505). */
  referPatient: [
    EpocusRole.Performer,
    EpocusRole.Reviewer,
    EpocusRole.PerformerReviewer,
  ],
  /**
   * See the Referrals section of the nav / surface. Distinct from
   * `referPatient` (the ACTION of referring a patient onward): this gates
   * visibility of the Referrals area itself. Performer-only: the reviewer MVP
   * nav is Home / Review Queue / My Performance / Settings, and program
   * oversight never saw it (PNE-531, narrowed by PNE-746).
   */
  viewReferrals: [EpocusRole.Performer, EpocusRole.PerformerReviewer],
  /** See the review queue. Reviewers and program oversight (PNE-505). */
  viewReviewQueue: [
    EpocusRole.Reviewer,
    EpocusRole.PerformerReviewer,
    EpocusRole.QaAdmin,
    EpocusRole.ProgramAdmin,
  ],
  /** Submit a review verdict. Reviewers and program oversight (PNE-505). */
  submitReview: [
    EpocusRole.Reviewer,
    EpocusRole.PerformerReviewer,
    EpocusRole.QaAdmin,
    EpocusRole.ProgramAdmin,
  ],
  /** View the program QA dashboard. Oversight roles only (PNE-505). */
  viewProgramDashboard: [EpocusRole.QaAdmin, EpocusRole.ProgramAdmin],
  viewQaWorkload: [EpocusRole.QaAdmin, EpocusRole.ProgramAdmin],
  viewCredentialing: [EpocusRole.QaAdmin, EpocusRole.ProgramAdmin],
  manageCredentials: [EpocusRole.ProgramAdmin],
  /**
   * Manage program users. Program Admin only (PNE-507).
   * BREAK IN CHAIN: QA Admin does NOT get this — user management is org-scoped
   * to PROGRAM_ADMIN in api-admin. Do not add EpocusRole.QaAdmin here.
   * (The FE hook `useCanManageProgramUsers` additionally lets internal
   * Elephant staff in — that override is deliberately outside this table.)
   */
  manageProgramUsers: [EpocusRole.ProgramAdmin],
  /**
   * See the Billing tab and send the day's batch to the billing company.
   * Program Admin only (PNE-751 product decision, 2026-09-18): there is no
   * billing-clerk or billing-company role this sprint, so the mockup's "solo
   * doctor" and "billing clerk" perspectives both collapse into this one.
   * BREAK IN CHAIN: QA Admin does NOT get this — billing is a commercial
   * action, not program oversight.
   */
  manageBilling: [EpocusRole.ProgramAdmin],
  /** View one's own performance stats. Every ePOCUS role (PNE-505). */
  viewOwnPerformance: [
    EpocusRole.Performer,
    EpocusRole.Reviewer,
    EpocusRole.PerformerReviewer,
    EpocusRole.QaAdmin,
    EpocusRole.ProgramAdmin,
  ],
} satisfies Record<string, readonly EpocusRole[]>;

export type Capability = keyof typeof CAPABILITIES;
